import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import NumberFormat from "react-number-format"
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import moment from "moment"
import { Link } from "react-router-dom"
import { ChevronDown, ChevronRight } from "lucide-react"
import "../EstadoResultado/estadoResultado.css"

export const SupplierTracking = () => {
    const money = useSelector(state => state.general.info)
    const [status, setStatus] = useState('por pagar')
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)
    const [search, setSearch] = useState('')
    const [horizon, setHorizon] = useState(3)
    const [showProjection, setShowProjection] = useState(false)
    const [openAll, setOpenAll] = useState(false)

    useEffect(() => {
        let active = true
        const load = async () => {
            const query = new URLSearchParams({ estado: status })
            const result = await Model.getAll({ url: `reports/supplier-tracking?${query}` })
            if (active) setReport({ status, refresh, result })
        }
        load()
        return () => { active = false }
    }, [status, refresh])

    const loaded = report?.status === status && report?.refresh === refresh
    const result = loaded ? report.result : null
    const totals = result?.totales || {}
    const pending = Number(totals.por_pagar)
    const overdue = Number(totals.vencido)
    const rows = (result?.data || []).filter(row => row.projects?.length > 0).sort((a, b) =>
        Number(b.total_late) - Number(a.total_late) || Number(b.total_por_pagar) - Number(a.total_por_pagar))
    const top = rows.filter(row => Number(row.total_late) > 0).slice(0, 5).map(row => ({
        ...row, label: (row.nombre_completo || 'Sin nombre').toLocaleUpperCase('es'), overdue: Number(row.total_late),
    }))
    const months = showProjection ? (result?.months || []).slice(0, horizon) : []
    const visible = rows.filter(row => (row.nombre_completo || '').toLocaleLowerCase('es').includes(search.trim().toLocaleLowerCase('es')))
        .map(row => ({
            ...row,
            nextMonths: row.nextMonths?.slice(0, months.length),
            projects: row.projects?.map(project => ({ ...project, nextMonths: project.nextMonths?.slice(0, months.length) })),
        }))

    const amount = value => (
        <NumberFormat displayType="text" value={value}
            thousandSeparator={money?.local_separacion_miles || ','}
            decimalSeparator={money?.local_separacion_decimales || '.'}
            prefix={`${money?.local_simbolo || 'S/'} `} decimalScale={2} fixedDecimalScale />
    )

    return (
        <div className="p-container er-report">
            <div className="mt-3">
                <Breadcrumb routes={[
                    { id: 1, name: 'Reportes', link: '/reportes', active: false },
                    { id: 2, name: 'Pendiente por pagar', link: '/reportes/st', active: true },
                ]} />
            </div>
            <header className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes de proveedores</span>
                    <h1 className="p-title">Pendiente por pagar</h1>
                    <p>¿Cuánto falta pagar, qué está vencido y qué pagos debemos priorizar?</p>
                </div>
                <div className="er-filters d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label" htmlFor="collection-status">Estado</label>
                        <select className="form-select" id="collection-status" value={status} onChange={event => setStatus(event.target.value)}>
                            <option value="por pagar">Por pagar</option>
                            <option value="pagado">Pagado</option>
                        </select>
                    </div>
                    <button type="button" className="btn p-btn-secondary" disabled={!loaded}
                        onClick={() => setRefresh(value => value + 1)}>Actualizar</button>
                </div>
            </header>

            {!loaded && <p role="status">Cargando reporte de pagos…</p>}
            {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}
            {result && (
                <>
                    <p className="er-source mb-3">
                        Importes totales en moneda local. Por pagar se calcula restando los pagos registrados al total proyectado.
                        Vencido se calcula según la fecha de proyección.
                    </p>
                    <div className="row g-2 mb-3">
                        {[
                            ['Por pagar', amount(pending)],
                            ['Vencido según proyección', amount(overdue)],
                            ['% vencido sobre pendiente', pending > 0 && overdue >= 0 && overdue <= pending
                                ? new Intl.NumberFormat('es-PE', { style: 'percent', maximumFractionDigits: 1 }).format(overdue / pending) : '—'],
                            ['Pagado', amount(totals.pagado)],
                        ].map(([label, value]) => (
                            <div className="col-6 col-lg-3" key={label}>
                                <div className="er-chart h-100 mb-0">
                                    <div className="er-source">{label}</div>
                                    <strong className="fs-5">{value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                    {top.length > 0 && (
                        <figure className="er-chart">
                            <figcaption className="fw-bold mb-2">Clientes con mayor importe vencido según proyección</figcaption>
                            <div className="er-chart-scroll">
                                <ResponsiveContainer width="100%" minWidth={340} height={top.length * 36 + 12}>
                                    <BarChart layout="vertical" data={top}>
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="label" width={155} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={value => amount(value)} />
                                        <Bar dataKey="overdue" name="Vencido" fill="#b02a37" maxBarSize={22} isAnimationActive={false} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="list-unstyled mb-0">
                                {top.map(row => <li key={row.id}><small>{row.label}</small> · {amount(row.overdue)}</li>)}
                            </ul>
                        </figure>
                    )}
                    {!rows.length ? <p role="status">No hay proveedores para el estado seleccionado.</p> : (
                        <>
                            <div className="d-flex flex-wrap gap-3 align-items-end mb-2">
                                <div>
                                    <label className="form-label" htmlFor="collection-search">Buscar proveedor</label>
                                    <input className="form-control" id="collection-search" type="search" value={search}
                                        onChange={event => setSearch(event.target.value)} />
                                </div>
                                <button type="button" className="btn p-btn-secondary" aria-pressed={showProjection}
                                    onClick={() => setShowProjection(value => !value)}>
                                    {showProjection ? 'Ocultar pagos proyectados' : 'Ver pagos proyectados'}
                                </button>
                                {showProjection && <div>
                                    <label className="form-label" htmlFor="collection-horizon">Horizonte</label>
                                    <select className="form-select" id="collection-horizon" value={horizon}
                                        onChange={event => setHorizon(Number(event.target.value))}>
                                        <option value={3}>3 meses</option>
                                        <option value={6}>6 meses</option>
                                        <option value={12}>12 meses</option>
                                    </select>
                                </div>}
                            </div>
                            {!visible.length ? <p role="status">No hay proveedores que coincidan con la búsqueda.</p> : (
                                <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Pagos por proveedor y proyecto">
                                    <table className="table er-table mb-0">
                                        <thead><HeadRows months={months} openAll={openAll} setOpenAll={setOpenAll} /></thead>
                                        <tbody><ItemsRows data={visible} openAll={openAll} /></tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}

const HeadRows = ({ openAll, setOpenAll, months }) => {
    return (
        <>
            {months.length > 0 && <tr>
                <th colSpan={5} className="sticky top-0 z-20 bg-white"></th>
                <th colSpan={months?.length} style={{ backgroundColor: '#FCF1FF' }} className="bg-[#FCF1FF] p-0">
                    <div className="w-full text-center text-[#8E24AA] py-2 px-4">
                        PAGOS PROYECTADOS
                    </div>
                </th>
            </tr >}
            <tr style={{ verticalAlign: 'middle', textTransform: 'uppercase', color: '#8E24AA' }}>
                <th
                    className="min-w-60 text-start cursor-pointer sticky left-[-25px] top-0 z-50 bg-white"
                    onClick={() => setOpenAll(!openAll)}
                >
                    <div className="d-flex justify-content-start">
                        <span>
                            {!openAll ?
                                <ChevronRight
                                    className="h-4 w-4 text-muted-foreground shrink-0 pointer"
                                />
                                :
                                <ChevronDown
                                    className="h-4 w-4 text-muted-foreground shrink-0 pointer"
                                />
                            }
                        </span>
                        <span className="ms-2">Proveedor / Proyecto</span>
                    </div>
                </th>
                <th className="min-w-24 sticky top-0 z-40 bg-white">Proyectado</th>
                <th className="min-w-24 sticky top-0 z-40 bg-white">Pagado</th>
                <th className="min-w-24 sticky top-0 z-40 bg-white">Por Pagar</th>
                <th className="min-w-24 sticky top-0 z-40 bg-white text-red-500">Vencido</th>
                {months.map((month, index) => (
                    <th key={index} className="min-w-24 sticky top-0 z-40 bg-white">
                        {moment(month).format('MMM') + ' ' + moment(month).format('YY')}
                    </th>
                ))}
            </tr>
        </>

    )
}

const ItemsRows = ({ data, openAll, handleOpenProject }) => {
    return (
        <>
            {data?.map(it => (
                <SupplierRow
                    key={it.id}
                    it={it}
                    projects={it.projects}
                    openAll={openAll}
                    handleOpenProject={handleOpenProject}
                />
            ))}
        </>
    )
}

const SupplierRow = ({ projects, it, openAll, handleOpenProject }) => {
    const [open, setOpen] = useState(false)
    const { local_simbolo, local_separacion_miles, local_separacion_decimales } = useSelector(state => state.general.info)

    return (
        <>
            <tr>
                <th
                    className="text-start cursor-pointer flex min-w-72 border-s sticky left-[-25px] z-30 bg-white"
                    onClick={() => setOpen(!open)}
                >
                    <span className="ms-2 flex items-center">
                        {!open ?
                            <ChevronRight
                                className="h-4 w-4 text-muted-foreground shrink-0 pointer"
                            />
                            :
                            <ChevronDown
                                className="h-4 w-4 text-muted-foreground shrink-0 pointer"
                            />
                        }
                    </span>
                    <div className="ms-1 flex flex-col my-1">
                        <span className="uppercase text-[12px] text-gray-700">{it?.nombre_completo}</span>
                        <span className="text-[9px] font-semibold text-gray">{it?.detail}</span>
                    </div>
                </th>
                <th className="min-w-24">
                    <NumberFormat
                        style={{ textAlign: 'right' }}
                        displayType={'text'}
                        value={it.total_proyectado || 0}
                        thousandSeparator={local_separacion_miles}
                        decimalSeparator={local_separacion_decimales}
                        prefix={`${local_simbolo} `}
                        decimalScale={2}
                        fixedDecimalScale={2}
                    />
                </th>
                <th className="min-w-24">
                    <NumberFormat
                        displayType={'text'}
                        value={it.total_pagado || 0}
                        thousandSeparator={local_separacion_miles}
                        decimalSeparator={local_separacion_decimales}
                        prefix={`${local_simbolo} `}
                        decimalScale={2}
                        fixedDecimalScale={2}
                    />
                </th>
                <th className="min-w-24">
                    <NumberFormat
                        displayType={'text'}
                        value={it.total_por_pagar || 0}
                        thousandSeparator={local_separacion_miles}
                        decimalSeparator={local_separacion_decimales}
                        prefix={`${local_simbolo} `}
                        decimalScale={2}
                        fixedDecimalScale={2}
                    />
                </th>
                <th className="min-w-24">
                    <NumberFormat
                        className={it?.total_late && parseFloat(it?.total_late) > 0 ? 'text-red-500' : ''}
                        displayType={'text'}
                        value={it?.total_late || 0}
                        thousandSeparator={local_separacion_miles}
                        decimalSeparator={local_separacion_decimales}
                        prefix={`${local_simbolo} `}
                        decimalScale={2}
                        fixedDecimalScale={2}
                    />
                </th>
                {it?.nextMonths?.map(m => (
                    <th key={m.id} className="border-s min-w-24">
                        <NumberFormat
                            style={{ textAlign: 'right', color: m?.amount > 0 ? '#8E24AA' : '' }}
                            displayType={'text'}
                            value={m.amount || 0}
                            thousandSeparator={local_separacion_miles}
                            decimalSeparator={local_separacion_decimales}
                            prefix={`${local_simbolo} `}
                            decimalScale={2}
                            fixedDecimalScale={2}
                        />
                    </th>
                ))}
            </tr >
            {(open || openAll) && projects?.map((item, index) => (
                <ProjectsRow
                    key={index}
                    item={item}
                    handleOpenProject={handleOpenProject}
                />
            ))
            }
        </>
    )
}

const ProjectsRow = ({ item, handleOpenProject }) => {
    const { local_simbolo, local_separacion_miles, local_separacion_decimales } = useSelector(state => state.general.info)

    return (
        <tr className="bg-gray-50 cursor-pointer text-[11px] font-medium border-s">
            <td className="min-w-72 sticky left-[-25px] z-30 bg-white">
                <Link
                    to={`/ppto/${item?.id}`}
                    target="_blank"
                >
                    <p className="text-start uppercase ms-4">{item?.referencia}</p>
                </Link>
            </td>
            <td className="min-w-24">
                <NumberFormat
                    displayType={'text'}
                    value={item?.total_proyectado || 0}
                    thousandSeparator={local_separacion_miles}
                    decimalSeparator={local_separacion_decimales}
                    prefix={`${local_simbolo} `}
                    decimalScale={2}
                    fixedDecimalScale={2}
                />
            </td>
            <td className="min-w-24">
                <NumberFormat
                    displayType={'text'}
                    value={item?.total_pagado || 0}
                    thousandSeparator={local_separacion_miles}
                    decimalSeparator={local_separacion_decimales}
                    prefix={`${local_simbolo} `}
                    decimalScale={2}
                    fixedDecimalScale={2}
                />
            </td>
            <td className="min-w-24">
                <NumberFormat
                    style={{ color: item?.total_por_pagar < 0 ? 'red' : '' }}
                    displayType={'text'}
                    value={item?.total_por_pagar || 0}
                    thousandSeparator={local_separacion_miles}
                    decimalSeparator={local_separacion_decimales}
                    prefix={`${local_simbolo} `}
                    decimalScale={2}
                    fixedDecimalScale={2}
                />
            </td>
            <td className="min-w-24">
                <NumberFormat
                    className={item?.total_late && parseFloat(item?.total_late) > 0 ? 'text-red-500' : ''}
                    displayType={'text'}
                    value={item?.total_late || 0}
                    thousandSeparator={local_separacion_miles}
                    decimalSeparator={local_separacion_decimales}
                    prefix={`${local_simbolo} `}
                    decimalScale={2}
                    fixedDecimalScale={2}
                />
            </td>
            {item?.nextMonths?.map(m => (
                <td key={m.id} className="border-s min-w-24">
                    <NumberFormat
                        style={{ textAlign: 'right', color: m?.amount > 0 ? '#8E24AA' : '' }}
                        displayType='text'
                        value={m?.amount || 0}
                        thousandSeparator={local_separacion_miles}
                        decimalSeparator={local_separacion_decimales}
                        prefix={`${local_simbolo} `}
                        decimalScale={2}
                        fixedDecimalScale={2}
                    />
                </td>
            ))}
        </tr>
    )
}